import { Link } from "react-router-dom";
import { ArrowRight, Flame, Snowflake, ClockClockwise, UsersThree, Waves, Star } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import QuietBayMark from "@/components/QuietBayMark";
import { api } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1505142468610-359e7d316be0?auto=format&fit=crop&w=1600&q=75";
const SAUNA_IMG = "https://images.pexels.com/photos/32504769/pexels-photo-32504769.jpeg";
const ICE_IMG = "https://images.unsplash.com/photo-1734117928667-c7f943a27e80";

export default function Landing() {
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    api.get("/reviews").then(({ data }) => setReviews(data)).catch(() => setReviews([]));
  }, []);

  return (
    <div data-testid="landing-page">
      {/* HERO — split layout, light theme */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <div className="fade-in flex items-center gap-3 mb-10">
              <QuietBayMark size={28} color="#9D968D" />
              <p className="overline" data-testid="hero-overline">Cascais · Oeiras · Sauna &amp; Banho de Gelo</p>
            </div>
            <h1 className="fade-in d-1 font-display text-6xl md:text-7xl lg:text-8xl leading-[0.92] tracking-tight text-[#003D4C]" data-testid="hero-title">
              Far Beyond
              <br />
              Hot <span className="amp">&amp;</span> Cold.
            </h1>
            <p className="fade-in d-2 mt-10 max-w-xl text-base text-[#003D4C]/70 leading-relaxed">
              Sauna a 90°C. Banho de gelo a 4°C. O oceano a respirar ao lado.
              Sessões de uma hora, até 8 pessoas, das 8h às 20h.
            </p>
            <div className="fade-in d-3 mt-10 flex flex-wrap gap-3">
              <Link to="/book" className="btn-hot px-7 py-3.5 inline-flex items-center gap-2 text-sm" data-testid="hero-cta-book">
                Reservar sessão <ArrowRight size={16} weight="light" />
              </Link>
              <a href="#services" className="btn-ghost px-7 py-3.5 text-sm" data-testid="hero-cta-services">
                Ver serviços
              </a>
            </div>

            <div className="fade-in d-4 mt-14 grid grid-cols-2 gap-4 max-w-md">
              <div className="border-t hairline pt-5" data-testid="kpi-capacity">
                <div className="flex items-center gap-2">
                  <UsersThree size={18} weight="light" color="#6BB9B8" />
                  <span className="overline">Capacidade</span>
                </div>
                <div className="font-display text-4xl text-[#003D4C] mt-2">8<span className="text-2xl text-[#003D4C]/50"> /h</span></div>
              </div>
              <div className="border-t hairline pt-5" data-testid="kpi-hours">
                <div className="flex items-center gap-2">
                  <ClockClockwise size={18} weight="light" color="#BE3A34" />
                  <span className="overline">Aberto</span>
                </div>
                <div className="font-display text-4xl text-[#003D4C] mt-2">08–20<span className="text-2xl text-[#003D4C]/50">h</span></div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 fade-in-right d-2">
            <div className="relative h-[560px] overflow-hidden">
              <img src={HERO} alt="Costa de Cascais ao nascer do sol" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#003D4C]/30 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-[#F5F1EA]">
                <p className="overline text-[#F5F1EA]/80">— Onde o calor abre,</p>
                <p className="font-display text-2xl mt-1">o frio sela e o oceano cuida do resto.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 border-t hairline">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4" data-reveal>
            <p className="overline mb-6">— Serviços</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05] text-[#003D4C]">
              Três rituais.
              <br />
              <span className="text-[#003D4C]/55">Um propósito.</span>
            </h2>
            <p className="text-sm text-[#003D4C]/65 mt-6 leading-relaxed max-w-xs">
              Cada serviço é entregue em sessões discretas, de uma hora, com lugar
              garantido junto à baía.
            </p>
          </div>
          <div className="lg:col-span-8 grid md:grid-cols-2 gap-5" data-reveal>
            <ServiceTile
              icon={<Flame size={24} weight="light" color="#BE3A34" />}
              title="Sauna"
              price="€25"
              desc="60 min · vapor seco a ~90°C. Abertura de poros, vasodilatação, libertação."
              img={SAUNA_IMG}
              testid="service-tile-sauna"
            />
            <ServiceTile
              icon={<Snowflake size={24} weight="light" color="#6BB9B8" />}
              title="Banho de Gelo"
              price="€10"
              desc="60 min · imersão a ~4°C. Contração, foco mental, resposta inflamatória."
              img={ICE_IMG}
              testid="service-tile-ice"
            />
            <div className="md:col-span-2 service-card flex items-center justify-between gap-6 flex-wrap" data-testid="service-tile-combo">
              <div className="flex items-center gap-4">
                <div className="flex gap-1">
                  <Flame size={24} weight="light" color="#BE3A34" />
                  <Snowflake size={24} weight="light" color="#6BB9B8" />
                </div>
                <div>
                  <div className="overline">Recomendado</div>
                  <div className="font-display text-3xl mt-1 text-[#003D4C]">Sauna + Banho de Gelo</div>
                  <p className="text-sm text-[#003D4C]/65 mt-1">O contraste térmico completo. Mesma hora, dois rituais.</p>
                </div>
              </div>
              <div className="text-right">
                <div className="font-display text-4xl text-[#003D4C]">€35</div>
                <Link to="/book" className="btn-hot inline-block mt-3 px-5 py-2 text-xs" data-testid="cta-combo-book">
                  Reservar
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS — dark contrast section */}
      <section className="dark-section relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="flex items-center gap-3 mb-14" data-reveal>
            <Waves size={22} weight="light" color="#6BB9B8" />
            <p className="overline" style={{ color: "rgba(185, 211, 220, 0.65)" }}>Como funciona</p>
          </div>
          <div className="grid md:grid-cols-4 gap-10">
            {[
              { n: "01", t: "Escolha", d: "Selecione sauna, banho de gelo ou o ciclo completo." },
              { n: "02", t: "Hora", d: "Veja a ocupação em tempo real e escolha um slot livre." },
              { n: "03", t: "Pague", d: "Pagamento seguro com cartão via Stripe." },
              { n: "04", t: "Aproveite", d: "Apresente-se 5 minutos antes. O resto é silêncio." },
            ].map((s, i) => (
              <div key={s.n} data-reveal style={{ transitionDelay: `${i * 90}ms` }} data-testid={`step-${s.n}`}>
                <div className="font-display text-6xl text-[#D69A2D] leading-none">{s.n}</div>
                <div className="mt-5 text-lg text-[#F5F1EA]">{s.t}</div>
                <p className="text-sm text-[#B9D3DC]/70 mt-2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      {reviews.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="reviews-section">
          <div className="grid lg:grid-cols-12 gap-10 mb-14" data-reveal>
            <div className="lg:col-span-6">
              <p className="overline mb-6">— O que dizem</p>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05] text-[#003D4C]">
                Vozes que <span className="text-[#003D4C]/55">passaram por aqui.</span>
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="reviews-grid">
            {reviews.slice(0, 6).map((r, i) => (
              <article key={r.review_id} data-reveal style={{ transitionDelay: `${i * 80}ms` }}
                className="card card-hover flex flex-col" data-testid={`review-${r.review_id}`}>
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <Star key={idx} size={14} weight={idx < r.rating ? "fill" : "regular"}
                      color={idx < r.rating ? "#D69A2D" : "#9D968D"} />
                  ))}
                </div>
                <p className="font-display text-xl text-[#003D4C] leading-snug flex-1">&ldquo;{r.comment}&rdquo;</p>
                <div className="mt-6 pt-4 border-t hairline-soft">
                  <div className="text-sm text-[#003D4C]">{r.user_name || "Visitante"}</div>
                  <div className="text-xs text-[#003D4C]/55 mt-0.5">{r.service_label}</div>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-32 text-center" data-reveal>
        <div className="flex justify-center"><QuietBayMark size={48} color="#9D968D" /></div>
        <p className="overline mt-8 mb-6">— Mind · Body · Spirit</p>
        <h2 className="font-display text-5xl md:text-6xl tracking-tight text-[#003D4C]">
          A próxima sessão começa <span className="text-[#003D4C]/55">agora.</span>
        </h2>
        <Link to="/book" className="btn-hot inline-flex items-center gap-2 mt-10 px-8 py-4" data-testid="cta-bottom-book">
          Ver disponibilidade <ArrowRight size={16} weight="light" />
        </Link>
      </section>
    </div>
  );
}

function ServiceTile({ icon, title, price, desc, img, testid }) {
  return (
    <div className="service-card flex flex-col" data-testid={testid}>
      <div className="relative h-48 -mx-7 -mt-7 mb-6 overflow-hidden">
        <img src={img} alt="" className="w-full h-full object-cover transition-transform duration-[1200ms] hover:scale-105" />
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {icon}
          <div className="font-display text-3xl text-[#003D4C]">{title}</div>
        </div>
        <div className="font-display text-3xl text-[#003D4C]">{price}</div>
      </div>
      <p className="text-sm text-[#003D4C]/65 mt-3 leading-relaxed">{desc}</p>
    </div>
  );
}
